import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import Toast from "primevue/toast";
import ToastService from "primevue/toastservice";
import ConfirmationService from "primevue/confirmationservice";
import PrimeVue from "primevue/config";
import Lara from "@primeuix/themes/lara";
import { createPinia } from "pinia";
import { useAuthStore } from "./stores/auth";
import { usePropsStore } from "./stores/props";
import axios from "axios";
import { usePassThrough } from "primevue/passthrough";
import Tooltip from "primevue/tooltip";
import { sso } from "./services/SSOService";
import type { ApiError } from "./types/Response";
import { PT } from "./assets/PrimeVuePassThrough";

//AG GRID
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
ModuleRegistry.registerModules([AllCommunityModule]);

//CSS/Themes
import "./assets/tailwind.css";
import "./assets/themes/coral.css";
import "./assets/themes/deepwoods.css";
import "./assets/themes/default.css";
import "./assets/themes/frost.css";
import "./assets/themes/minty.css";
import "./assets/themes/shipwreck.css";
import "./assets/themes/steely.css";
import { Role } from "./router/Role";
import type { Tokens } from "./types/Token";
import type { Response } from "./types/Response";
import { waitUntil } from "./services/Utility";

const pinia = createPinia();
const app = createApp(App);

app.directive("tooltip", Tooltip);
app.directive("visible", function (el, binding) {
  el.style.visibility = !!binding.value ? "visible" : "hidden";
});

const CustomPreset = usePassThrough(Lara, PT);
//DIDN'T IMPLEMENT THE SPEEDIAL SEVERITY SYSTEM YET
//TODO: Make disabled state look better, currently tough to see well
app.use(PrimeVue, {
  theme: {
    preset: Lara,
    options: {
      darkModeSelector: ".dark",
      cssLayer: {
        name: "primevue",
        order: "theme, base, primevue",
      },
    },
  },
  pt: CustomPreset,
  ptOptions: {
    mergeSections: true,
    mergeProps: true,
  },
});

app.use(ConfirmationService);
app.use(ToastService);
app.use(pinia);
app.use(router);
app.provide("sso", sso);

const apiRequestNoRolePrefix = [
  "/admin-login",
  "/portal-login",
  "/account/changepass",
  "/account/resetpass",
  "/account/passwordreqs",
  "/draw-files",
  "/refreshtoken",
  "/admin-sso-login",
];
const apiNoAuth = ["/admin-login", "/portal-login", "/account/resetpass", "/account/passwordreqs", "/refreshtoken", "/admin-sso-login"];

const propsStore = usePropsStore();

const traceUrl = (url: string, message: string | null = null) => {
  if (propsStore.traceApiCalls) {
    if (message != null) {
      console.log(message + ": " + url);
    } else {
      console.log(url);
    }
  }
};

router.beforeEach((to, from) => {
  //SSO Specific login callback
  if (to.path === "/login") {
    return sso
      .handleLoginRedirect()
      .then(() => {})
      .catch((error) => {
        console.log(error);
        return { path: "/" };
      });
  }

  const authStore = useAuthStore();

  if (from.path.startsWith("/project-dashboard") && authStore.token !== null) {
    axios.post(`/projects/${from.params.id}/unlock`).then((response) =>
      handleApiResponse(
        response,
        (data: any) => {},
        (errors: ApiError[]) => {
          if (errors.length > 0) error("Error unlocking project", errors[0].message);
        }
      )
    );
  }

  if (!to.meta.requiredRole) return true;

  if (authStore.token === null) {
    console.log("Path Being Redirected");
    return { path: "/" };
  }

  if (authStore.forcePasswordChange && to.name !== "change-pass" && to.name !== "portal-change-pass") {
    return { name: authStore.getUserInfo().scope !== "ADMIN" ? "change-pass" : "portal-change-pass" };
  }

  if (
    (to.meta.requiredRole === Role.ADMIN && authStore.getUserInfo().scope !== "ADMIN") ||
    (to.meta.requiredRole === Role.USER && authStore.getUserInfo().scope !== "USER")
  ) {
    console.log("Restricted");
    return { name: "access-denied" };
  }
});

const toLocalISOString = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    date.getFullYear() +
    "-" +
    pad(date.getMonth() + 1) +
    "-" +
    pad(date.getDate()) +
    "T" +
    pad(date.getHours()) +
    ":" +
    pad(date.getMinutes()) +
    ":" +
    pad(date.getSeconds())
  );
};

//Dates were being always set to UTC when parsed and throwing off the time, and eventually the date after enough passes
const convertDates = (value: any): any => {
  if (value instanceof Date) return toLocalISOString(value);
  if (Array.isArray(value)) return value.map(convertDates);
  if (value && typeof value === "object") {
    const result: any = {};
    for (const key in value) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        result[key] = convertDates(value[key]);
      }
    }
    return result;
  }
  return value;
};

axios.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore();
    if (authStore.token) {
      let useAuth = true;
      for (let url of apiNoAuth) {
        if ((config.url ?? "").startsWith(url)) {
          useAuth = false;
          break;
        }
      }
      if (useAuth) {
        config.headers["Authorization"] = `Bearer ${authStore.token}`;
      }
    }

    let completeUrl: boolean = (config.url ?? "").startsWith("http://") || (config.url ?? "").startsWith("https://");

    if (!completeUrl && config.url !== "/properties.json") {
      let prepend = true;

      for (let url of apiRequestNoRolePrefix) {
        if ((config.url ?? "").startsWith(url)) {
          prepend = false;
          break;
        }
      }

      if (prepend) {
        if (authStore.getUserInfo().scope == "ADMIN") {
          config.url = "/admin" + config.url;
        } else if (authStore.getUserInfo().scope == "USER") {
          config.url = "/portal" + config.url;
        }
      }

      const propsStore = usePropsStore();
      config.url = propsStore.api + config.url;
    }

    traceUrl(config.url!);

    const isPlainObject: boolean =
      config.data !== null &&
      typeof config.data === "object" &&
      config.headers["Content-Type"] !== "multipart/form-data" &&
      !(config.data instanceof FormData) &&
      !(config.data instanceof ArrayBuffer) &&
      !(config.data instanceof Blob);
    if (isPlainObject || Array.isArray(config.data)) config.data = convertDates(config.data);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (res) => {
    const isExpired = res.data.errors?.find((err: ApiError) => err.code == 4) !== undefined;
    if (isExpired) {
      router.push("license-key-upload");
    }
    return res;
  },
  async (err) => {
    const originalConfig = err.config;
    let originalUrl = originalConfig.url?.replace(propsStore.api, "") ?? "";
    const authStore = useAuthStore();
    let role = authStore.getUserInfo().scope;

    let pathUsesAuth = true;
    for (let url of apiNoAuth) {
      if (originalUrl.startsWith(url)) {
        pathUsesAuth = false;
        break;
      }
    }

    if (pathUsesAuth && err.response) {
      // Access Token was expired
      if (err.response.status === 401) {
        if (authStore.refreshing) {
          // Already refreshing, try to wait until refresh is complete, then try again
          traceUrl(originalConfig.url, "Already refreshing token, waiting for refresh to finish");
          await waitUntil(() => !authStore.refreshing);
          if (authStore.hasAuth()) {
            traceUrl(originalConfig.url, "Refreshed while waiting, trying again");
            return axios(originalConfig);
          } else {
            traceUrl(originalConfig.url, "Logged out while waiting, aborting");
            return Promise.reject(err);
          }
        }
        authStore.refreshing = true;

        try {
          traceUrl(originalConfig.url, "Trying to refresh token");
          const response: Response = (await axios.post("/refreshtoken?token=" + authStore.refreshToken)).data;
          if (response.errors != null && response.errors.length > 0) {
            traceUrl(originalConfig.url, "Refresh token expired, logging out");
            authStore.expireSession();
            error("Session expired", "Your session has expired.");
            router.push({
              name: role === "USER" ? "portal-login" : "admin-login",
            });
            return Promise.reject(response.errors[0]);
          }
          const tokens: Tokens = response.result;
          authStore.token = tokens.accessToken;
          authStore.refreshToken = tokens.refreshToken;
          authStore.refreshing = false;
          authStore.sessionExpired = false;

          traceUrl(originalConfig.url, "Refresh successful");
          return axios(originalConfig);
        } catch (_error) {
          traceUrl(originalConfig.url, "Error when refreshing, logging out");
          authStore.expireSession();
          error("Session expired", "Your session has expired.");
          router.push({
            name: role === "USER" ? "portal-login" : "admin-login",
          });
          return Promise.reject(_error);
        }
      }
    } else if (originalUrl.startsWith("/refreshtoken")) {
      traceUrl(originalConfig.url, "An error occurred while performin the refresh, logging out");
      authStore.expireSession();
      error("Session expired", "Your session has expired.");
      router.push({
        name: role === "USER" ? "portal-login" : "admin-login",
      });
    }

    return Promise.reject(err);
  }
);

//Set up property variables
axios
  .get("/properties.json")
  .then((response) => {
    propsStore.api = response.data.API_URL;
    propsStore.clientName = response.data.CLIENT_NAME;
    propsStore.traceApiCalls = response.data.TRACE_API_CALLS ?? false;
    propsStore.maxFileSize = response.data.MAX_FILE_SIZE ?? 104857600;
    propsStore.ssoEnabled = response.data.SSO_ENABLED ?? false;
    propsStore.ssoDomain = response.data.SSO_DOMAIN ?? "";
    propsStore.ssoClientId = response.data.SSO_CLIENT_ID ?? "";
    propsStore.ssoIssuer = response.data.SSO_ISSUER ?? "";
    propsStore.ssoJwksUri = response.data.SSO_JWKS_URI ?? "";
    propsStore.ssoEndSessionEndpoint = response.data.SSO_END_SESSION_ENDPOINT ?? "";
    propsStore.ssoAuthorizationEnpoint = response.data.SSO_AUTHORIZATION_ENDPOINT ?? "";
    propsStore.ssoTokenEndpoint = response.data.SSO_TOKEN_ENDPOINT ?? "";
    propsStore.logo = response.data.LOGO != null ? "images/" + response.data.LOGO : "/images/logo.png";
    propsStore.loginBg = response.data.LOGIN_BG != null ? "images/" + response.data.LOGIN_BG : "/images/login-bg.jpg";
    propsStore.demo = response.data.DEMO;
    propsStore.streamline = response.data.STREAMLINE;
  })
  .catch((e) => console.log(e));
document.documentElement.setAttribute("data-theme", propsStore.theme);
if (propsStore.darkMode) {
  document.documentElement.classList.toggle("dark");
}

app.component("Toast", Toast);

app.mount("#app");
